import PdfPreview from "./PdfPreview"
import { useId } from "react"
import { handleFileDrop } from "../../utils/fileUploadUtils"

export default function FileUploadBox({
  label,
  clickLabel = "click to select",
  helperText = "Allowed: PNG, JPG, SVG or PDF - max. 2 MB",
  uploadedFile,
  previewUrl,
  error,
  onFileSelect,
  onDelete,
  accept = "image/png,image/jpeg,image/svg+xml,application/pdf",
  preview = true,
  disabled = false,
  className = "",
  linkClassName = "text-orange-600 hover:underline",
}: {
  label?: string
  clickLabel?: string
  helperText?: string
  uploadedFile: File | null
  previewUrl?: string | null
  error: string
  onFileSelect: (file: File) => void
  onDelete?: () => void
  accept?: string
  preview?: boolean
  disabled?: boolean
  className?: string
  linkClassName?: string
}) {
  const inputId = useId()
  const hasPreview = !!uploadedFile || !!previewUrl

  const isPdf = uploadedFile && (uploadedFile.type === "application/pdf" || uploadedFile.name.toLowerCase().endsWith(".pdf"))

  if (isPdf) {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between gap-6 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="min-w-0 flex-1">
            <PdfPreview file={uploadedFile} />
          </div>
          {onDelete && !disabled && (
            <button type="button" onClick={onDelete} className="shrink-0 text-sm text-red-600 hover:underline">
              Delete file
            </button>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>
    )
  }

  if (hasPreview) {
    return (
      <div className="text-center mb-4">
        {uploadedFile && <PdfPreview file={uploadedFile} />}
        {preview && previewUrl && uploadedFile?.type !== "application/pdf" && (
          <img
            src={previewUrl}
            alt="Preview"
            className="mx-auto h-64 object-contain border rounded-md"
          />
        )}

        {uploadedFile && uploadedFile.type !== "application/pdf" && !uploadedFile.name.toLowerCase().endsWith(".pdf") && (
          <p className="text-sm text-gray-600 mt-2">{uploadedFile.name}</p>
        )}

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        {onDelete && !disabled && (
          <button
            type="button"
            onClick={onDelete}
            className="mt-2 text-sm text-red-500 hover:underline"
          >
            Delete file
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => !disabled && handleFileDrop(e, onFileSelect)}
      className={`border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-sm text-gray-600 mb-4 cursor-pointer hover:border-orange-400 transition-colors ${className} ${disabled ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
    >
      <input
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file && !disabled) onFileSelect(file)
          e.target.value = ""
        }}
        className="hidden"
      />

      <label htmlFor={inputId} className={`cursor-pointer ${disabled ? "pointer-events-none" : ""}`}>
        <img src="/upload.png" alt="Upload icon" className="h-8 mx-auto mb-2 opacity-80" />
        {label || "Drag & drop your file here or "}
        <span className={linkClassName}>{clickLabel}</span>.
      </label>

      <p className="text-xs text-gray-500 mt-2">{helperText}</p>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </div>
  )
}
